import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { flashcards as flashcardsAPI } from '../../services/api';
import { useConfirm } from '../../hooks/useConfirm';
import ConfirmModal from '../Common/ConfirmModal';
import '../Conversation/ConversationPage.css';

const FlashcardsPage = () => {
  const [flashcards, setFlashcards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newCard, setNewCard] = useState({ front: '', back: '', context: '' });
  const navigate = useNavigate();
  const { confirm, confirmState, handleConfirm, handleCancel } = useConfirm();

  useEffect(() => {
    loadFlashcards();
  }, []);

  const loadFlashcards = async () => {
    try {
      const response = await flashcardsAPI.getAll();
      setFlashcards(response.data);
    } catch (error) {
      console.error('Failed to load flashcards:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFlashcard = async (e) => {
    e.preventDefault();
    try {
      await flashcardsAPI.create({
        ...newCard,
        language: 'es',
        difficulty: 'medium',
      });
      setNewCard({ front: '', back: '', context: '' });
      setShowCreateDialog(false);
      loadFlashcards();
    } catch (error) {
      console.error('Failed to create flashcard:', error);
    }
  };

  const handleDeleteFlashcard = async (id) => {
    const confirmed = await confirm({
      title: 'Delete Flashcard',
      message: 'Are you sure you want to delete this flashcard? This action cannot be undone.',
      confirmText: 'Delete',
      confirmStyle: 'danger'
    });

    if (!confirmed) return;

    try {
      await flashcardsAPI.delete(id);
      loadFlashcards();
    } catch (error) {
      console.error('Failed to delete flashcard:', error);
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading">Loading flashcards...</div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <header className="page-header">
        <button className="btn-back" onClick={() => navigate('/home')}>
          ← Back
        </button>
        <h1>Flashcards</h1>
      </header>

      <main className="page-content">
        <button className="btn-create" onClick={() => setShowCreateDialog(true)}>
          + Create New Flashcard
        </button>

        <div className="conversations-list">
          {flashcards.length === 0 ? (
            <div className="empty-state">
              <p>No flashcards yet</p>
              <p className="subtitle">Create your first flashcard to start learning!</p>
            </div>
          ) : (
            flashcards.map((card) => (
              <div key={card.id} className="conversation-item" style={{ flexDirection: 'column' }}>
                <div>
                  <h3 style={{ fontSize: '18px', marginBottom: '8px' }}>
                    {card.front}
                  </h3>
                  <p style={{ color: '#757575', marginBottom: '12px' }}>
                    {card.back}
                  </p>
                  {card.context && (
                    <p style={{ fontSize: '12px', color: '#9e9e9e', marginBottom: '12px' }}>
                      Context: {card.context}
                    </p>
                  )}
                  <p style={{ fontSize: '12px', color: '#9e9e9e' }}>
                    Reviews: {card.reviewCount || 0}
                  </p>
                </div>
                <button
                  onClick={() => handleDeleteFlashcard(card.id)}
                  style={{
                    padding: '8px 16px',
                    background: '#ffebee',
                    color: '#d32f2f',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    marginTop: '12px'
                  }}
                >
                  Delete
                </button>
              </div>
            ))
          )}
        </div>
      </main>

      {showCreateDialog && (
        <div className="modal-overlay" onClick={() => setShowCreateDialog(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Create Flashcard</h3>
            <form onSubmit={handleCreateFlashcard}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                  Front (Word/Phrase)
                </label>
                <input
                  type="text"
                  value={newCard.front}
                  onChange={(e) => setNewCard({ ...newCard, front: e.target.value })}
                  required
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #e0e0e0',
                    borderRadius: '8px',
                    fontSize: '16px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                  Back (Translation/Definition)
                </label>
                <input
                  type="text"
                  value={newCard.back}
                  onChange={(e) => setNewCard({ ...newCard, back: e.target.value })}
                  required
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #e0e0e0',
                    borderRadius: '8px',
                    fontSize: '16px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                  Context (Optional)
                </label>
                <textarea
                  value={newCard.context}
                  onChange={(e) => setNewCard({ ...newCard, context: e.target.value })}
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #e0e0e0',
                    borderRadius: '8px',
                    fontSize: '16px',
                    boxSizing: 'border-box',
                    resize: 'vertical'
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  type="submit"
                  style={{
                    flex: 1,
                    padding: '12px',
                    background: '#1976D2',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  Create
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateDialog(false)}
                  style={{
                    flex: 1,
                    padding: '12px',
                    background: 'white',
                    color: '#757575',
                    border: '2px solid #e0e0e0',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      <ConfirmModal
        isOpen={confirmState.isOpen}
        title={confirmState.title}
        message={confirmState.message}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        confirmText={confirmState.confirmText}
        cancelText={confirmState.cancelText}
        confirmStyle={confirmState.confirmStyle}
      />
    </div>
  );
};

export default FlashcardsPage;
